import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { MemberType, Team, UserStatus, Squad } from 'api/models';
import { AssigneeValue } from 'api/tasks';
import { Label } from 'components/atoms/label';
import { TemplateAssigneePicker } from 'components/molecules/assignee-picker/template-assignee-picker';
import { SquadValue } from 'components/molecules/squad-picker';
import { squadPayloadToSquad } from 'components/molecules/squad-picker/utils';
import { RootState } from 'data';
import { useTemplateMentionsData } from 'data/templates/hooks';
import {
  ProjectTemplateTask,
  saveTemplatePlaceholder,
} from 'data/templates/projects';
import { getActiveTemplatePlaceholders } from 'data/templates/projects/selectors';
import { DetailViewTitleInput } from '../title-input';
import { ResponsibleDropdown } from './responsible-dropdown/responsible-dropdown';
import {
  ContentWrapper,
  HeaderSection,
  HeaderSectionWrapper,
  StatusIndicator,
  SubHeaderSection,
  Wrapper,
} from './styles';

export const TemplateTaskHeader: FC<{
  moreActions?: JSX.Element;
  task: Partial<ProjectTemplateTask>;
  onUpdateName(text: string): void;
  onUpdateAssignee(value?: SquadValue): void;
  onChangeResponsibility(value: MemberType[]): void;
}> = ({
  moreActions,
  task,
  onUpdateName,
  onUpdateAssignee,
  onChangeResponsibility = () => {},
}) => {
  const dispatch = useDispatch();
  const { members } = useTemplateMentionsData();
  const [assignee, setAssignee] = useState<AssigneeValue | undefined>();

  const { byId: teamsById } = useSelector(({ teams }: RootState) => teams);

  const templatePlaceholders = useSelector(getActiveTemplatePlaceholders());

  const placeholders = useMemo(
    () => ({
      ...teamsById,
      ...templatePlaceholders,
    }),
    [teamsById, templatePlaceholders],
  );

  /**
   * Filtering out deleted users and placeholders
   * who were previously selected.
   * Since template is managed by FE, this is inevitable.
   */
  useEffect(() => {
    if (!task.assignee) {
      setAssignee(undefined);

      return;
    }
    const { users, teams } = task.assignee;
    const activeUserIds = members.items.users
      .filter(({ userStatus }) => userStatus !== UserStatus.Inactive)
      .map(({ userId }) => userId);

    setAssignee({
      users: users?.filter(({ userId }) => activeUserIds.includes(userId)),
      teams: teams?.filter(({ teamId }) => !!placeholders[teamId]),
    });
  }, [task.assignee, placeholders, members.items.users]);

  const updateResponsibility = useCallback(
    (squad: Squad) => {
      const getResponsibleOn = () => [
        ...new Set([
          ...squad?.teams?.map((team) => team.teamType),
          ...squad?.users?.map(
            (user) => user?.userType as unknown as MemberType,
          ),
        ]),
      ];

      const result = getResponsibleOn();

      onChangeResponsibility(result);
    },
    [onChangeResponsibility],
  );

  const _onPlaceholderCreate = useCallback(
    (team: Team) => {
      onUpdateAssignee(
        squadPayloadToSquad(
          {
            users: [
              ...(assignee?.users || []).map((_user) => ({
                userId: _user.userId,
              })),
            ],
            teams: [
              ...(assignee?.teams || []).map((_team) => ({
                teamId: _team.teamId,
              })),
              { teamId: team.teamId },
            ],
          },
          {
            ...members.items,
            teams: [...Object.values(placeholders), team] as Team[],
          },
        ),
      );
      dispatch(saveTemplatePlaceholder(team));
    },
    [
      onUpdateAssignee,
      assignee?.users,
      assignee?.teams,
      members.items,
      placeholders,
      dispatch,
    ],
  );

  const onAssigneeChange = useCallback(
    (selected) => {
      const newAssignee = squadPayloadToSquad(selected, {
        ...members.items,
        teams: Object.values(placeholders),
      });

      updateResponsibility(newAssignee);

      return onUpdateAssignee(newAssignee);
    },
    [members.items, onUpdateAssignee, placeholders, updateResponsibility],
  );

  return (
    <Wrapper>
      <StatusIndicator
        progress={0}
        status={{
          label: 'To do',
          group: 1,
          color: 'blue',
        }}
        type={task.type}
      />
      <ContentWrapper>
        <HeaderSectionWrapper>
          <HeaderSection>
            <DetailViewTitleInput
              value={task.taskName || ''}
              updateValue={onUpdateName}
            />
          </HeaderSection>
          {moreActions}
        </HeaderSectionWrapper>

        <SubHeaderSection>
          <div>
            <Label>
              <FormattedMessage id="assigned_to" />
            </Label>
            <TemplateAssigneePicker
              responsibleTeam={
                task.private
                  ? [MemberType.Native]
                  : [MemberType.External, MemberType.Native]
              }
              value={assignee}
              onDone={onAssigneeChange}
              onPlaceholderCreate={_onPlaceholderCreate}
              isPrivate={task.private}
            />
          </div>
          {assignee?.users.length || assignee?.teams.length ? (
            <div>
              <Label>
                <FormattedMessage id="responsible" />
              </Label>
              <ResponsibleDropdown value={task.responsibleOn} />
            </div>
          ) : null}
        </SubHeaderSection>
      </ContentWrapper>
    </Wrapper>
  );
};
